import { Employee, Event, getCampaignEdition, GeoveloIcon } from '@geovelo-frontends/commons';
import { Avatar, Box, CircularProgress, Typography } from '@mui/material';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { AppContext } from '../../app/context';
import illu from '../../assets/img/login-illu.svg';
import { AppInlineIcon, AppInlineWhiteIcon } from '../../components';
import ImpersonatingBanner from '../../components/impersonating-banner';
import useImpersonating from '../../hooks/impersonating';
import usePartnerSubgroupsQuery from '../../hooks/use-partner-subgroups-query';

import AppointmentBooking from './appointment-booking';
import BringTogether from './bring-together';
import Steps from './steps';

export const registerStep = { key: 'register', label: 'Création de compte' };

export const loginStep = { key: 'login', label: 'Connexion au compte Geovelo' };

export type TOnboardingStepKey = 'personal-data' | 'addresses' | 'team' | 'transport-modes';

export const onboardingSteps: Array<{ key: TOnboardingStepKey; labelKey: string }> = [
  { key: 'personal-data', labelKey: 'companies.pages.onboarding.steps_labels.personal_data' },
  {
    key: 'addresses',
    labelKey: 'companies.pages.onboarding.steps_labels.addresses',
  },
  {
    key: 'team',
    labelKey: 'companies.pages.onboarding.steps_labels.team',
  },
  {
    key: 'transport-modes',
    labelKey: 'companies.pages.onboarding.transport_modes.title',
  },
];

const onboardingStepOrder = onboardingSteps.map(({ key }) => key);

type TOnboardingCompletenessArgs = {
  employee: Employee;
  teams?: Array<{ id: number }> | null;
};

function isStepIncomplete(
  step: TOnboardingStepKey,
  { employee, teams }: TOnboardingCompletenessArgs,
): boolean {
  switch (step) {
    case 'personal-data':
      return (
        !employee.hasFirstName ||
        !employee.hasLastName ||
        !employee.hasProfessionalEmail ||
        !employee.eulaAccepted ||
        !employee.privacyPolicyAccepted
      );
    case 'addresses':
      return employee.isCollaborator && !employee.hasAtLeastOneCommutingTrip;
    case 'team':
      return (
        employee.isCollaborator &&
        Boolean(employee.departmentIsMissing) &&
        Boolean(teams && teams.length > 0)
      );
    case 'transport-modes':
      return employee.isCollaborator && !employee.hasTransportHabits;
    default:
      return false;
  }
}

export function getNextOnboardingPath(
  afterStep: TOnboardingStepKey,
  args: TOnboardingCompletenessArgs & { currentEvent?: Event | null },
): string {
  const remainingSteps = onboardingStepOrder.slice(onboardingStepOrder.indexOf(afterStep) + 1);

  const nextIncompleteStep = remainingSteps.find((step) => isStepIncomplete(step, args));

  return nextIncompleteStep
    ? `/onboarding/${nextIncompleteStep}`
    : args.currentEvent
      ? `/events/${args.currentEvent.id}`
      : '/get-started';
}

export function getCompletedOnboardingStepKeys(args: TOnboardingCompletenessArgs): string[] {
  return onboardingStepOrder.filter((step) => !isStepIncomplete(step, args));
}

export const addCompanySteps = [
  { key: 'personal-data', labelKey: 'companies.pages.new-company.steps_labels.personal_data' },
  { key: 'company-data', labelKey: 'companies.pages.new-company.steps_labels.company_data' },
  { key: 'sites', labelKey: 'companies.pages.new-company.steps_labels.sites', optional: true },
  { key: 'members', labelKey: 'companies.pages.new-company.steps_labels.members', optional: true },
];

export const addCompanyStepsWithoutMembers = addCompanySteps.filter((s) => s.key !== 'members');

function LoginLayout({
  isSSO,
  leftPanelType,
  isWebview,
  partnerIcon,
  illustration,
  steps: _steps,
  stepIndex,
  rightPanelColor,
  rightPanelFullHeight,
  disableScroll,
  hideStepCounter,
  backButton,
  title,
  titleVariant,
  eventCode,
  children,
}: {
  backButton?: ReactNode;
  children?: ReactNode;
  disableScroll?: boolean;
  hideStepCounter?: boolean;
  eventCode?: string;
  isSSO?: boolean;
  isWebview?: boolean;
  illustration?: string;
  leftPanelType: 'bringTogether' | 'stepper' | 'default' | 'appointmentBooking';
  partnerIcon?: ReactNode | null;
  rightPanelColor?: string;
  rightPanelFullHeight?: boolean;
  stepIndex?: number;
  steps?: Array<{ key: string } & ({ label: ReactNode } | { labelKey: string })>;
  title: ReactNode;
  titleVariant?: 'h5' | 'h6' | 'body1' | 'body2';
}): React.JSX.Element {
  const {
    app: { currentEvent: contextCurrentEvent },
    partner: { current: currentPartner },
    user: { employee, team: userTeam },
  } = useContext(AppContext);
  const { data: partnerSubgroups } = usePartnerSubgroupsQuery(currentPartner);
  const teams = partnerSubgroups?.teams;
  const completedStepKeys = employee ? getCompletedOnboardingStepKeys({ employee, teams }) : [];
  const [searchParams] = useSearchParams();
  const eventIdInUrl = searchParams.get('event_id') ?? sessionStorage.getItem('event_id');
  const eventLoading = !!eventIdInUrl && contextCurrentEvent === undefined;
  const isBfgEventLoading = eventLoading && (eventIdInUrl === '50' || eventIdInUrl === '307');
  const version = isBfgEventLoading
    ? 'bfg_2026'
    : (eventCode ?? contextCurrentEvent?.code?.toLowerCase());
  const { t } = useTranslation();
  const { impersonating } = useImpersonating();
  const edition = getCampaignEdition(version);

  const steps = _steps?.filter(
    ({ key }) => key !== 'team' || (teams && teams.length > 0 && !userTeam),
  );

  return (
    <Box
      flexGrow={1}
      id="container"
      sx={disableScroll ? { height: '100%', overflow: 'hidden' } : { overflowY: 'auto' }}
    >
      {impersonating && <ImpersonatingBanner />}
      <Box
        bgcolor={isSSO ? '#f3f6ff' : undefined}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        {...(disableScroll ? { height: '100%', overflow: 'hidden' } : { minHeight: '100%' })}
      >
        {!isSSO && eventLoading && !isBfgEventLoading && (
          <Box
            alignItems="center"
            display={{ xs: 'none', md: isWebview ? 'none' : 'flex' }}
            justifyContent="center"
            width="50%"
          >
            <CircularProgress />
          </Box>
        )}
        {!isSSO && (!eventLoading || isBfgEventLoading) && (
          <Box
            alignItems="center"
            display={{ xs: 'none', md: isWebview ? 'none' : 'flex' }}
            flexDirection="column"
            gap={6}
            sx={
              leftPanelType === 'appointmentBooking'
                ? { backgroundColor: '#d9e7ff' }
                : edition?.panelBackgroundColor
                  ? {
                      backgroundColor: edition.panelBackgroundColor,
                      overflow: edition.panelOverflowHidden ? 'hidden' : undefined,
                    }
                  : {
                      background:
                        version === 'bttf'
                          ? 'linear-gradient(161deg, #6210FF 0%, #47A3FE 76.08%, #2FECC3 109.89%)'
                          : 'linear-gradient(135deg, #326EBF 0%, #326AC2 38%, #2DA599 100%)',
                      color: '#fff',
                    }
            }
            width={edition?.panelWidth || '50%'}
          >
            {leftPanelType === 'bringTogether' || isBfgEventLoading ? (
              <BringTogether eventCode={isBfgEventLoading ? 'bfg_2026' : eventCode} />
            ) : leftPanelType === 'stepper' || (leftPanelType === 'default' && version) ? (
              <Steps
                completedKeys={completedStepKeys}
                currentStepIndex={stepIndex || 0}
                eventCode={eventCode}
                items={steps || []}
              />
            ) : leftPanelType === 'appointmentBooking' ? (
              <AppointmentBooking />
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                minHeight="max(100svh, 100%)"
                width="100%"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                  gap={3}
                  justifyContent="center"
                  paddingX={{ xs: 5, lg: 8 }}
                  paddingY={5}
                >
                  <AppInlineWhiteIcon sx={{ height: 34, width: (34 / 35) * 261 }} />
                  <Typography color="#fff" fontSize="1.15rem" fontWeight={600}>
                    Tous en selle pour la transition écologique !
                  </Typography>
                </Box>
                <Box
                  height={300}
                  sx={{
                    backgroundImage: `url(${illu})`,
                    backgroundPosition: 'bottom left',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <Box
          bgcolor={rightPanelColor}
          display="flex"
          flexDirection="column"
          flexGrow={1}
          justifyContent={isSSO ? 'center' : undefined}
          minHeight={disableScroll ? 0 : undefined}
          sx={{ overflowX: 'hidden', ...(disableScroll ? { overflow: 'hidden' } : {}) }}
          width={{ md: isWebview ? '100%' : isSSO ? '100%' : '50%' }}
        >
          <Box
            alignSelf="center"
            bgcolor={isSSO ? '#fff' : undefined}
            borderRadius={isSSO ? 6 : 0}
            display="flex"
            flexDirection="column"
            flexGrow={isSSO ? 0 : 1}
            gap={disableScroll ? 2 : 5}
            justifyContent={{ xs: 'flex-start', md: isWebview ? 'flex-start' : 'center' }}
            maxWidth={isSSO ? 600 : 'calc(100% - 32px)'}
            minHeight={disableScroll ? 0 : undefined}
            paddingX={isSSO ? 5 : 0}
            paddingY={isSSO ? 5 : disableScroll ? 1 : 3}
            width={isSSO ? 600 : '75%'}
          >
            {!isWebview && (
              <>
                {partnerIcon ? (
                  typeof partnerIcon === 'string' ? (
                    <Avatar
                      slotProps={{
                        img: { sx: { objectFit: 'contain', height: '90%', width: '90%' } },
                      }}
                      src={partnerIcon}
                      sx={{ border: '1px solid #e3e7ee', height: 54, flexShrink: 0, width: 54 }}
                    />
                  ) : (
                    partnerIcon
                  )
                ) : isSSO ? (
                  <GeoveloIcon sx={{ height: 40, flexShrink: 0, width: 40 }} />
                ) : (
                  <AppInlineIcon sx={{ height: 35, flexShrink: 0, width: 261 }} />
                )}
              </>
            )}
            <Box
              display="flex"
              flexDirection="column"
              flexGrow={illustration || rightPanelFullHeight || disableScroll ? 1 : undefined}
              gap={disableScroll ? 2 : 5}
              justifyContent={illustration ? 'center' : 'flex-start'}
              minHeight={disableScroll ? 0 : undefined}
              overflow={disableScroll ? 'hidden' : undefined}
            >
              {illustration && (
                <Box alignSelf="center" flexShrink={0} height={200}>
                  <img height="100%" src={illustration} />
                </Box>
              )}
              <Box display="flex" flexDirection="column" flexShrink={0} gap={1}>
                {backButton}
                {steps && !hideStepCounter && (
                  <Typography color="textSecondary" variant="body2">
                    {t('companies.step', {
                      stepIndex: (stepIndex || 0) + 1,
                      stepsCount: steps.length,
                    })}
                  </Typography>
                )}
                <Typography
                  align={illustration ? 'center' : 'left'}
                  component="h1"
                  fontWeight={800}
                  variant={titleVariant ?? (illustration ? 'body1' : 'h5')}
                >
                  {title}
                </Typography>
              </Box>
              {disableScroll ? (
                <Box
                  flex="1 1 0"
                  minHeight={0}
                  overflow="hidden auto"
                  sx={{ scrollbarWidth: 'none' }}
                >
                  {children}
                </Box>
              ) : (
                children
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default LoginLayout;
